import React, { useState } from "react";
import { FormOrganizerProps } from "interfaces/forms/FormOrganizerProps";
import { FormViewer } from "./FormViewer";
import FormEditor from "./editor/FormEditor";
import { Form } from "models/forms/Form";
import FormList from "./FormList";
import { utilities } from "utils/utilities";
import { ApiService } from "services/ApiService";
import { useParams } from "react-router-dom";

export const FormOrganizer: React.FC<FormOrganizerProps> = ({ ownerId }) => {
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const { teamId } = useParams<{ teamId: string }>();

  const isSplitView = Boolean(selectedFormId) || isEditing;

  const handleCreateNew = () => {
    setSelectedFormId(utilities.DEFAULT_GUID);
    setIsEditing(true);
  };

  const handleFormSelectionClick = (formId: string | Form) => {
    const idToSet = typeof formId === "string" ? formId : formId.id;

    setSelectedFormId(idToSet);
    setIsEditing(false);
  };

  const handleEditButtonClick = () => {
    if (selectedFormId.length === 0) return;
    setIsEditing(true);
  };

  if (!teamId) return null;

  const handleDelete = async () => {
    const response = (await ApiService.deleteForm(teamId, selectedFormId)).success;

    setSelectedFormId("");
    setIsEditing(false);

    handleCRUD_Operations();
  }

  // This method here is so that the formlist can be updated if
  // a new form was created or deleted.
  const handleCRUD_Operations: any = () => {
    setCount((c) => c + 1);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {!isSplitView && (
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">Forms Organizer</h1>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <FormList
                ownerId={ownerId}
                onCreateNew={handleCreateNew}
                onSelect={handleFormSelectionClick}
                useDtos={true}
                formListCount={count}
              />
            </div>
          </div>
        )}

        {isSplitView && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Side: Form List (wider so cards never cut off) */}
            <div className="w-full lg:w-1/3 lg:min-w-[360px]">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <h1 className="text-xl font-semibold mb-3 text-center">Forms Organizer</h1>
                <FormList
                  ownerId={ownerId}
                  onCreateNew={handleCreateNew}
                  onSelect={handleFormSelectionClick}
                  useDtos={true}
                  formListCount={count}
                />
              </div>
            </div>

            {/* Right Side: Viewer or Editor */}
            <div className="flex-1 min-w-0">
              {selectedFormId && !isEditing && (
                <div className="w-full">
                  {/* Form Viewer Box */}
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
                    <FormViewer formId={selectedFormId} form={null} isDisabled={false} />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleEditButtonClick}
                      className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Edit
                    </button>
                    <button
                      id={selectedFormId}
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}

              {isEditing && (
                <FormEditor
                  formId={selectedFormId}
                  onSaveNewOrDelete={handleCRUD_Operations}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );


};
